import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSummary, getWarnings, getNotes, getCommendations, getAttendance } from '../lib/db.js';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';
import { tenureLabel, thaiDate } from '../lib/format.js';

export function EmployeeProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sum, setSum] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [commends, setCommends] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, w, n, c, a] = await Promise.all([
          getSummary(id), getWarnings(id), getNotes(id), getCommendations(id), getAttendance(id),
        ]);
        if (!active) return;
        setSum(s);
        setWarnings(w);
        setNotes(n);
        setCommends(c);
        setAttendance(a);
      } catch (e) {
        if (active) setErr(e.message);
      }
    })();
    return () => { active = false; };
  }, [id]);

  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <button className="back" onClick={() => nav(-1)}>‹ กลับ</button>
        {err && <p className="err">{err}</p>}
        {!sum && !err && <div className="center"><div className="spinner" /></div>}

        {sum && (
          <>
            <div className="title-row">
              <div>
                <div className="eyebrow">{[sum.position, sum.branch].filter(Boolean).join(' · ') || '—'}</div>
                <h1 className="page">{sum.nickname || `${sum.first_name} ${sum.last_name}`}</h1>
              </div>
              <span className="glyph">○</span>
            </div>

            <div className="hero">
              <div className="num small">{tenureYears(sum.tenure_days)}</div>
              <div className="sub">อายุงาน {tenureLabel(sum.tenure_days)} · เริ่ม {thaiDate(sum.start_date)}</div>
            </div>

            <hr className="hr" />

            <div className="grid">
              <Stat k="มาสาย (ปีนี้)" v={sum.late_count_ytd ?? 0} unit="ครั้ง" />
              <Stat k="ขาด (ปีนี้)" v={sum.absent_count_ytd ?? 0} unit="ครั้ง" />
              <Stat pip="warn" k="ใบเตือนมีผล" v={sum.active_warnings ?? 0} unit="ฉบับ" />
              <Stat pip="ok" k="ชมเชย" v={sum.commendation_count ?? 0} unit="ครั้ง" />
              <Stat pip="ok" k="แต้มรางวัล" v={sum.reward_points ?? 0} unit="แต้ม" />
              <Stat k="สถานะ" v={statusTh(sum.status)} />
            </div>

            <div className="act-row">
              <Link to={`/issue-warning?emp=${id}`}>
                <button className="cta ghost act-btn">⚠︎ ใบเตือน</button>
              </Link>
              <Link to={`/recognize`}>
                <button className="cta ghost act-btn">★ ชมเชย</button>
              </Link>
              <Link to={`/evaluations/${id}`}>
                <button className="cta ghost act-btn">◷ ประเมิน</button>
              </Link>
            </div>

            <div className="sec-label">ใบเตือนที่ยังมีผล</div>
            {warnings.length === 0 && <p className="muted">ไม่มีใบเตือนมีผล</p>}
            {warnings.map((w) => (
              <Link className="row" key={w.id} to={`/warnings/${w.id}`}>
                <div className="ic">⚠︎</div>
                <div className="main">
                  <div className="l1">{w.incident_detail || 'หนังสือตักเตือน'}</div>
                  <div className="l2">ออก {thaiDate(w.incident_date)} · ขั้นที่ {w.ladder_level}{w.level_name ? ` (${w.level_name})` : ''}</div>
                </div>
                <span className="tag warn">ดู ›</span>
              </Link>
            ))}

            <div className="sec-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span>สถิติเข้างาน · ซิงค์จาก HR Time Tracker</span>
              <Link to={`/attendance/${id}`} style={{ color: 'var(--t2)', fontWeight: 600 }}>รายเดือน ›</Link>
            </div>
            {attendance.length === 0 && <p className="muted">ไม่มีรายการสาย/ขาด/ออกก่อน 👍</p>}
            {attendance.map((a) => (
              <div className="row" key={a.id}>
                <div className="ic">{attGlyph(a.flag_type)}</div>
                <div className="main">
                  <div className="l1">{attLabel(a)}</div>
                  <div className="l2">
                    {thaiDate(a.work_date)}
                    {a.is_excused ? ` · ${a.leave_label || 'ลาที่อนุมัติแล้ว'}` : ''}
                  </div>
                </div>
                {a.is_excused
                  ? <span className="tag ok">ลา · ไม่นับ</span>
                  : <span className="tag warn">ความผิด</span>}
              </div>
            ))}

            <div className="sec-label">ผลงาน &amp; ชมเชย</div>
            {commends.length === 0 && <p className="muted">ยังไม่มีใบชมเชย</p>}
            {commends.map((c) => (
              <div className="row" key={c.id}>
                <div className="ic" style={{ color: 'var(--ok)' }}>★</div>
                <div className="main">
                  <div className="l1">{c.title}</div>
                  <div className="l2">{catLabel(c.category)} · {thaiDate(c.occurred_on)}</div>
                </div>
                {c.reward_points > 0
                  ? <span className="tag reward-tag">+{c.reward_points} แต้ม</span>
                  : <span className="tag ok">ชมเชย</span>}
              </div>
            ))}

            <div className="sec-label">บันทึกล่าสุด</div>
            {notes.length === 0 && <p className="muted">ยังไม่มีบันทึก</p>}
            {notes.map((n) => (
              <div className="row" key={n.id}>
                <div className="ic" style={{ color: noteColor(n.note_type) }}>{noteGlyph(n.note_type)}</div>
                <div className="main">
                  <div className="l1">{n.title}</div>
                  <div className="l2">{thaiDate(n.occurred_on)}</div>
                </div>
                <span className={`tag ${noteTag(n.note_type)}`}>{noteLabel(n.note_type)}</span>
              </div>
            ))}
          </>
        )}
      </section>
      <BottomNav />
    </div>
  );
}

function Stat({ pip, k, v, unit }) {
  return (
    <div className="stat">
      <div className="k">{pip && <span className={`pip ${pip}`} />}{k}</div>
      <div className="v">{v} {unit && <small>{unit}</small>}</div>
    </div>
  );
}
function tenureYears(days) {
  if (days == null) return '—';
  return Math.max(0, Math.floor(days / 365));
}
function statusTh(s) {
  return { active: 'ทำงาน', probation: 'ทดลอง', inactive: 'พัก', terminated: 'พ้นสภาพ' }[s] || s || '—';
}
const catLabel = (c) => ({ performance: 'ผลงานดีเด่น', service: 'บริการลูกค้า', teamwork: 'ช่วยทีม', other: 'อื่นๆ' }[c] || 'ชมเชย');
const attGlyph = (t) => ({ late: '◔', absent: '○', early_leave: '◑' }[t] || '·');
const attLabel = (a) => {
  if (a.flag_type === 'late') return `มาสาย ${a.minutes} นาที`;
  if (a.flag_type === 'early_leave') return `ออกก่อนเวลา ${a.minutes} นาที`;
  if (a.flag_type === 'absent') return 'ขาดงาน';
  return a.flag_type;
};
const noteColor = (t) => (t === 'positive' ? 'var(--ok)' : t === 'negative' ? 'var(--warn)' : 'var(--t2)');
const noteGlyph = (t) => (t === 'positive' ? '＋' : t === 'negative' ? '－' : '·');
const noteTag = (t) => (t === 'positive' ? 'ok' : t === 'negative' ? 'warn' : 'pend');
const noteLabel = (t) => (t === 'positive' ? 'ผลงาน' : t === 'negative' ? 'ตักเตือน' : 'บันทึก');
